import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Repeat, Loader2, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { productsAPI, cartAPI } from '../services/api';
import { availabilityOf } from '../services/stock';
import { productPath } from '../services/productPath';
import { swapStore } from '../services/savings';
import { useT } from '../services/i18n';

const CartAlternativeHint = ({ item, onReplaced }) => {
  const { t } = useT();
  const [alt, setAlt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAlt(null);
    if (item.pack_id) return undefined;
    productsAPI.getEquivalents(item.product_id).then(d => {
      if (cancelled) return;
      const best = (d.items || []).filter(p => p.price < item.price && availabilityOf(p).status !== 'out').sort((a, b) => a.price - b.price)[0];
      setAlt(best || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [item.product_id, item.price, item.pack_id]);

  if (!alt) return null;
  const multiple = alt.sale_multiple || 1;
  const qty = Math.max(multiple, Math.ceil(item.quantity / multiple) * multiple);
  const saving = item.price * item.quantity - alt.price * qty;

  const replace = async () => {
    setBusy(true);
    try {
      await cartAPI.addItem(alt.id, qty);
      await cartAPI.removeItem(item.product_id, null);
      swapStore.record(alt.id, { from_id: item.product_id, from_name: `${item.brand} ${item.name}`, saved_per_unit: Math.round((item.price - alt.price) * 100) / 100 });
      toast.success(`${t('alt.replaced', { name: item.name, alt: `${alt.brand} ${alt.name}` })}${saving > 0 ? ` · −${saving.toFixed(2)} €` : ''}`);
      await onReplaced?.();
    } catch (e) {
      toast.error(e.message || t('alt.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid={`cart-alternative-${item.product_id}`}>
      <Link to={productPath(alt)} className="flex items-center gap-2 min-w-0 flex-1 group" data-testid={`cart-alternative-link-${item.product_id}`}>
        <img src={alt.image} alt="" className="w-10 h-10 object-contain bg-white rounded" />
        <div className="min-w-0">
          <p className="text-xs text-blue-700 font-medium flex items-center gap-1"><Repeat size={12} /> {t('alt.cheaper', { brand: alt.brand })}</p>
          <p className="text-sm text-gray-800 truncate group-hover:underline">{alt.name} · <span className="font-bold">{alt.price.toFixed(2)} €</span> <span className="text-green-700 text-xs">({t('alt.perUnit', { d: (item.price - alt.price).toFixed(2) })}{saving > 0 && item.quantity > 1 ? t('alt.total', { s: saving.toFixed(2) }) : ''})</span></p>
        </div>
      </Link>
      <button type="button" onClick={replace} disabled={busy} data-testid={`cart-replace-${item.product_id}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#1e3a5f] hover:bg-[#2d4a6f] disabled:opacity-60 rounded-md px-3 py-1.5">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />} {t('alt.replace')}{qty !== item.quantity ? ` (×${qty})` : ''}
      </button>
    </div>
  );
};

export default CartAlternativeHint;
